/**
 * GDI Audience Targeting — pure matcher + tree filter (React-free, isomorphic).
 *
 * Every GDI block carries `${__typename}AudienceTargeting` = {Type, VariantId}.
 *
 *   Authentication Status  VariantId "loggedin" | "guest"
 *   Customer Group         VariantId = SFCC group ID. "Everyone"/empty → everyone;
 *                          "Registered" / "Guest" / "Unregistered" are system
 *                          groups resolved from isLoggedIn, not membership.
 *   AB Test                VariantId = "flagKey:variationKey"
 *
 * Unknown Type / empty value / unparseable variant → render for everyone.
 * Shared by the page hook, preview and the render markers (gdi-audience-markers.js).
 */

export const AUDIENCE_TARGETING_TYPE_AUTHENTICATION_STATUS = 'Authentication Status'
export const AUDIENCE_TARGETING_TYPE_CUSTOMER_GROUP = 'Customer Group'
export const AUDIENCE_VARIANT_LOGGED_IN = 'loggedin'
export const AUDIENCE_VARIANT_GUEST = 'guest'
export const AUDIENCE_VARIANT_EVERYONE = 'everyone'
// SFCC system customer groups that map directly to auth state.
export const AUDIENCE_VARIANT_REGISTERED = 'registered'
export const AUDIENCE_VARIANT_UNREGISTERED = 'unregistered'

/** Tolerates the CMS UI's `AB Test` and the TSD's `A/B Test` spellings. */
export const isAbTestType = (type) =>
    typeof type === 'string' && type.replace(/[^a-z0-9]/gi, '').toLowerCase() === 'abtest'

/** `flagKey:variationKey`, split on the FIRST `:` (flag keys may contain `-`, never `:`). */
export const parseAbVariantId = (variantId) => {
    if (typeof variantId !== 'string') return null
    const separatorIndex = variantId.indexOf(':')
    if (separatorIndex === -1) return null
    const flagKey = variantId.slice(0, separatorIndex).trim()
    const variationKey = variantId.slice(separatorIndex + 1).trim()
    if (!flagKey || !variationKey) return null
    return {flagKey, variationKey}
}

/** Read `*AudienceTargeting` off a node by suffix, without knowing its __typename. */
export const readAudienceTargeting = (node) => {
    if (!node || typeof node !== 'object') return undefined
    for (const [key, value] of Object.entries(node)) {
        if (key.endsWith('AudienceTargeting') && value && typeof value === 'object') {
            return value
        }
    }
    return undefined
}

/** Targeting on a content-area entry itself, or on its expanded block. */
export const readEntryTargeting = (entry) =>
    readAudienceTargeting(entry) || readAudienceTargeting(entry?.ContentLink?.Expanded)

/**
 * Which audience DIMENSION a targeting value depends on, normalised.
 * System customer groups classify as `auth` because that is what they resolve from.
 *
 * @param {{Type?: string, VariantId?: string} | null | undefined} targeting
 * @returns {{dim: 'auth', variant: string} | {dim: 'group', groupId: string} | {dim: 'ab', flagKey: string, variationKey: string} | null}
 *   `null` = not targeted / unresolvable → always render.
 */
export const classifyAudienceTargeting = (targeting) => {
    if (!targeting || typeof targeting !== 'object') return null
    const {Type, VariantId} = targeting

    if (isAbTestType(Type)) {
        const parsed = parseAbVariantId(VariantId)
        return parsed ? {dim: 'ab', ...parsed} : null
    }

    const variant = String(VariantId ?? '').trim()
    const lower = variant.toLowerCase()

    if (Type === AUDIENCE_TARGETING_TYPE_AUTHENTICATION_STATUS) {
        if (lower === AUDIENCE_VARIANT_LOGGED_IN || lower === AUDIENCE_VARIANT_GUEST) {
            return {dim: 'auth', variant: lower}
        }
        return null
    }

    if (Type === AUDIENCE_TARGETING_TYPE_CUSTOMER_GROUP) {
        if (!variant || lower === AUDIENCE_VARIANT_EVERYONE) return null
        if (lower === AUDIENCE_VARIANT_REGISTERED) {
            return {dim: 'auth', variant: AUDIENCE_VARIANT_LOGGED_IN}
        }
        if (lower === AUDIENCE_VARIANT_GUEST || lower === AUDIENCE_VARIANT_UNREGISTERED) {
            return {dim: 'auth', variant: AUDIENCE_VARIANT_GUEST}
        }
        return {dim: 'group', groupId: variant}
    }

    return null
}

/* auth / group evaluation from a classified target. A/B is not decided here. */
const targetMatches = (target, {isLoggedIn, customerGroups} = {}) => {
    if (target.dim === 'auth') {
        return Boolean(isLoggedIn) === (target.variant === AUDIENCE_VARIANT_LOGGED_IN)
    }
    if (target.dim === 'group') {
        return Array.isArray(customerGroups) && customerGroups.includes(target.groupId)
    }
    return true
}

/**
 * Pure check — `true` when the block should render for the supplied state.
 * Unknown / empty targeting always renders so missing config never hides content.
 *
 * @param {{Type?: string, VariantId?: string} | null | undefined} audienceTargeting
 * @param {{isLoggedIn?: boolean, customerGroups?: string[]}} [state]
 * @returns {boolean}
 */
export const matchesAudienceTargeting = (audienceTargeting, state = {}) => {
    const target = classifyAudienceTargeting(audienceTargeting)
    return target ? targetMatches(target, state) : true
}

/**
 * Resolves the `${__typename}AudienceTargeting` field a GDI block carries and
 * returns whether the block should render for the supplied state.
 *
 * @param {{__typename?: string} | null | undefined} block
 * @param {{isLoggedIn?: boolean, customerGroups?: string[]}} [state]
 * @returns {boolean}
 */
export const matchesBlockAudienceTargeting = (block, state) => {
    if (!block || typeof block !== 'object' || !block.__typename) return true
    return matchesAudienceTargeting(block[`${block.__typename}AudienceTargeting`], state)
}

/* Resolution status per dimension:
 *   pending     → hold (drop now, reappears on the next render)
 *   resolved    → evaluate
 *   unavailable → keep (fail open — will never resolve this session) */
const DEFAULT_STATUS = {auth: 'resolved', group: 'resolved', ab: 'unavailable'}

const warned = new Set()
const warnOnce = (key, message) => {
    if (warned.has(key) || process.env.NODE_ENV === 'production') return
    warned.add(key)
    console.warn(`[GDI audience] ${message}`)
}

const entryPasses = (entry, state, status, keptAb) => {
    const target = classifyAudienceTargeting(readEntryTargeting(entry))
    if (!target) return true

    const dimStatus = status[target.dim]
    if (dimStatus === 'pending') return false
    if (dimStatus === 'unavailable') {
        if (target.dim === 'ab') {
            warnOnce(
                `off:${target.flagKey}`,
                `A/B unavailable — rendering every arm of "${target.flagKey}"`
            )
        }
        return true
    }
    if (target.dim !== 'ab') return targetMatches(target, state)

    if (state.knownFlagKeys && !state.knownFlagKeys.includes(target.flagKey)) {
        warnOnce(`unknown:${target.flagKey}`, `unknown flag "${target.flagKey}" — block kept`)
        return true
    }
    const keep = state.decisions?.[target.flagKey] === target.variationKey
    if (keep) keptAb.push([target.flagKey, target.variationKey])
    return keep
}

const EMPTY_ARMS = Object.freeze([])
const filterCache = new WeakMap() // tree → {state, result}
const renderedAbArms = new WeakMap() // result tree → [[flagKey, variationKey]]

/**
 * Drop the content-area entries a shopper should not see. Pure: returns the
 * input itself when nothing is dropped (structural sharing), memoised on the
 * identity of (tree, state) so ~30 consumers of one tree share a single pass.
 *
 * @param {*} content CMS tree or any subtree
 * @param {{
 *   renderAll?: boolean,
 *   isLoggedIn?: boolean,
 *   customerGroups?: string[],
 *   decisions?: Object<string, string>,
 *   knownFlagKeys?: string[],
 *   status?: {auth?: string, group?: string, ab?: string}
 * }} [state] `renderAll` returns the input untouched (SSR + hydration render).
 * @returns {*} filtered tree, or the input when nothing applies
 */
export const filterAudienceTargeting = (content, state = {}) => {
    if (!content || typeof content !== 'object' || state.renderAll) return content

    const cached = filterCache.get(content)
    if (cached && cached.state === state) return cached.result

    const status = {...DEFAULT_STATUS, ...state.status}
    const keptAb = []

    const transform = (node) => {
        if (Array.isArray(node)) {
            let changed = false
            const next = []
            for (const entry of node) {
                if (!entryPasses(entry, state, status, keptAb)) {
                    changed = true
                    continue
                }
                const out = transform(entry)
                if (out !== entry) changed = true
                next.push(out)
            }
            return changed ? next : node
        }
        if (node && typeof node === 'object') {
            let changed = false
            const next = {}
            for (const [key, value] of Object.entries(node)) {
                const out = transform(value)
                if (out !== value) changed = true
                next[key] = out
            }
            return changed ? next : node
        }
        return node
    }

    const result = transform(content)
    filterCache.set(content, {state, result})
    renderedAbArms.set(result, keptAb.length ? keptAb : EMPTY_ARMS)
    return result
}

/** The A/B arms the filter kept in `tree` — the ones actually rendered, for impressions. */
export const getRenderedAbArms = (tree) => (tree && renderedAbArms.get(tree)) || EMPTY_ARMS
